import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Navigation } from "@/components/sections/Navigation";
import { Footer } from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { 
  Users, 
  ArrowLeft, 
  ArrowRight,
  Share2,
  Mail,
  Home,
  ChevronRight,
  ChevronLeft,
  ExternalLink,
  MessageSquare,
  Send,
  Lock,
  Sparkles,
  Copy,
  Check
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";

// Custom icons
const TelegramIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const WhatsAppIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const XIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

interface SocialGroupData {
  id: string;
  name_ar: string;
  name_en: string;
  description_ar: string;
  description_en: string;
  type: string;
  link: string;
  members: number;
  image_url: string | null;
  created_at: string;
}

const SocialGroupDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRTL = i18n.language === "ar";
  
  const [group, setGroup] = useState<SocialGroupData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
  const [registrationType, setRegistrationType] = useState<string>("");

  const platformColors = group?.type === "whatsapp" 
    ? { primary: '#25D366', secondary: '#128C7E', light: '#DCF8C6' }
    : { primary: '#0088cc', secondary: '#229ED9', light: '#A8D8FF' };

  useEffect(() => {
    const fetchGroup = async () => {
      if (!id) return;
      
      try {
        const { data, error } = await supabase
          .from("social_groups")
          .select("*")
          .eq("id", id)
          .maybeSingle();

        if (error) throw error;
        if (!data) {
          toast.error(isRTL ? "لم يتم العثور على المجموعة" : "Group not found");
          navigate("/social-groups");
          return;
        }
        setGroup(data);
      } catch (error) {
        console.error("Error fetching group:", error);
        toast.error(isRTL ? "لم يتم العثور على المجموعة" : "Group not found");
        navigate("/social-groups");
      } finally {
        setIsLoading(false);
      }
    };

    fetchGroup();
  }, [id, navigate, isRTL]);

  const triggerJoinConfetti = useCallback(() => {
    confetti({
      particleCount: 60,
      spread: 70,
      origin: { x: 0.5, y: 0.6 },
      colors: group?.type === "whatsapp" 
        ? ['#25D366', '#128C7E', '#075E54', '#DCF8C6'] 
        : ['#0088cc', '#229ED9', '#54A9EB', '#A8D8FF'],
      shapes: ['circle'],
      scalar: 0.8,
      gravity: 0.9,
      ticks: 150
    });
  }, [group?.type]);

  const handleJoinGroup = () => {
    if (!user) {
      setIsLoginPromptOpen(true);
      return;
    }

    if (!group) return;

    triggerJoinConfetti();
    setTimeout(() => {
      window.open(group.link, "_blank");
    }, 300);
  };

  const handleLoginPromptNavigate = useCallback(() => {
    setIsLoginPromptOpen(false);
    navigate("/auth", { state: { redirectTo: `/social-group/${id}` } });
  }, [navigate, id]);

  const handleRegistrationClick = () => {};

  if (isLoading) {
    return (
      <div className={`min-h-screen flex flex-col ${isRTL ? "rtl" : "ltr"}`}>
        <Navigation 
          isRTL={isRTL}
          setRegistrationType={setRegistrationType}
          handleRegistrationClick={handleRegistrationClick}
        />
        <main className="flex-1 container mx-auto px-4 py-8">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-96 w-full rounded-2xl mb-6" />
          <Skeleton className="h-12 w-64 mb-4" />
          <Skeleton className="h-24 w-full" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!group) {
    return null;
  }

  const name = isRTL ? group.name_ar : group.name_en;
  const description = isRTL ? group.description_ar : group.description_en;
  const shareUrl = `https://visitevent.live/social-group/${group.id}`;
  const imageUrl = group.image_url || "https://visitevent.live/og-image.png";
  const platformName = group.type === "whatsapp" ? (isRTL ? "واتساب" : "WhatsApp") : (isRTL ? "تيليجرام" : "Telegram");

  // Share message for social platforms
  const shareMessage = isRTL 
    ? `💬 انضم لمجموعتنا على ${platformName}!\n\n📢 ${name}\n👥 ${group.members.toLocaleString()} عضو\n\n🔗 سجل الآن للانضمام:`
    : `💬 Join our ${platformName} community!\n\n📢 ${name}\n👥 ${group.members.toLocaleString()} members\n\n🔗 Register now to join:`;

  const encodedShareMessage = encodeURIComponent(shareMessage);
  const encodedShareUrl = encodeURIComponent(shareUrl);

  const registrationBenefits = [
    {
      icon: MessageSquare,
      title: isRTL ? "الانضمام للمجموعات" : "Join Groups",
      description: isRTL ? "انضم لمجتمعات الفعاليات" : "Join event communities"
    },
    {
      icon: Users,
      title: isRTL ? "تواصل مع الأعضاء" : "Connect with Members",
      description: isRTL ? "تعرف على أشخاص جدد" : "Meet new people"
    },
    {
      icon: Sparkles,
      title: isRTL ? "مميزات حصرية" : "Exclusive Features",
      description: isRTL ? "وصول لمحتوى مميز" : "Access to premium content"
    }
  ];

  return (
    <div className={`min-h-screen flex flex-col ${isRTL ? "rtl" : "ltr"}`}>
      <Helmet>
        <title>{name} - {platformName} | VisitEvent</title>
        <meta name="description" content={description || `${isRTL ? 'انضم لمجموعتنا على' : 'Join our community on'} ${platformName}`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={`💬 ${isRTL ? 'انضم للمجموعة' : 'Join Community'}: ${name}`} />
        <meta property="og:description" content={`${isRTL ? `👥 ${group.members.toLocaleString()} عضو | انضم لمجتمعنا على ${platformName}` : `👥 ${group.members.toLocaleString()} members | Join our ${platformName} community`}`} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="VisitEvent" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={shareUrl} />
        <meta name="twitter:title" content={`💬 ${isRTL ? 'انضم للمجموعة' : 'Join Community'}: ${name}`} />
        <meta name="twitter:description" content={`${isRTL ? `👥 ${group.members.toLocaleString()} عضو | سجل الآن للانضمام` : `👥 ${group.members.toLocaleString()} members | Register now to join`}`} />
        <meta name="twitter:image" content={imageUrl} />
        
        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": name,
            "description": description,
            "image": imageUrl,
            "url": shareUrl,
            "memberOf": {
              "@type": "Organization",
              "name": "VisitEvent",
              "url": "https://visitevent.live"
            }
          })}
        </script>
      </Helmet>

      {/* Login Prompt Dialog */}
      <Dialog open={isLoginPromptOpen} onOpenChange={setIsLoginPromptOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 rounded-3xl" style={{
          background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
          boxShadow: '20px 20px 60px #d1d1d1, -20px -20px 60px #ffffff'
        }}>
          <div className="relative p-6 text-white text-center" style={{
            background: `linear-gradient(135deg, ${platformColors.primary}, ${platformColors.secondary})`
          }}>
            <div className="absolute inset-0 bg-black/10" />
            <motion.div 
              initial={{ scale: 0 }} 
              animate={{ scale: 1 }} 
              transition={{ type: "spring", delay: 0.1 }}
              className="relative"
            >
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-1">
                {isRTL ? "انضم إلينا!" : "Join Us!"}
              </h3>
              <p className="text-white/80 text-sm">
                {isRTL ? "سجل للانضمام للمجموعات" : "Register to join groups"}
              </p>
            </motion.div>
          </div>

          <div className="p-5 space-y-3">
            {registrationBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="flex items-center gap-4 p-3 rounded-2xl"
                style={{
                  background: 'linear-gradient(145deg, #f8f8f8, #ffffff)',
                  boxShadow: '3px 3px 8px #e8e8e8, -3px -3px 8px #ffffff'
                }}
              >
                <div 
                  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${platformColors.light}, ${platformColors.light}50)` }}
                >
                  <benefit.icon className="w-5 h-5" style={{ color: platformColors.primary }} />
                </div>
                <div className={isRTL ? "text-right" : "text-left"}>
                  <h4 className="font-semibold text-gray-700 text-sm">{benefit.title}</h4>
                  <p className="text-xs text-gray-500">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="p-5 pt-2 space-y-3">
            <Button 
              onClick={handleLoginPromptNavigate}
              className="w-full py-6 rounded-2xl text-base font-semibold text-white transition-all"
              style={{
                background: `linear-gradient(135deg, ${platformColors.primary}, ${platformColors.secondary})`,
                boxShadow: '5px 5px 15px #c0c0c0, -5px -5px 15px #ffffff'
              }}
            >
              {isRTL ? "سجل الآن مجاناً" : "Register Now - Free"}
            </Button>
            <button 
              onClick={() => setIsLoginPromptOpen(false)}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              {isRTL ? "لاحقاً" : "Maybe Later"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Navigation 
        isRTL={isRTL}
        setRegistrationType={setRegistrationType}
        handleRegistrationClick={handleRegistrationClick}
      />

      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="bg-muted/30 border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary flex items-center gap-1">
                <Home className="w-4 h-4" />
                {isRTL ? "الرئيسية" : "Home"}
              </Link>
              {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <Link to="/social-groups" className="hover:text-primary">
                {isRTL ? "مجموعات التواصل" : "Social Groups"}
              </Link>
              {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span className="text-foreground font-medium truncate max-w-[200px]">{name}</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <div className="relative">
          <div 
            className="absolute inset-0"
            style={{ background: `linear-gradient(135deg, ${platformColors.primary}, ${platformColors.secondary})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
          
          <div className="relative container mx-auto px-4 py-12 md:py-20">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Image/Icon */}
              <motion.div 
                className="flex justify-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div 
                  className="relative rounded-3xl overflow-hidden shadow-2xl"
                  style={{
                    boxShadow: `0 25px 50px -12px ${platformColors.secondary}80`
                  }}
                >
                  {group.image_url ? (
                    <img 
                      src={group.image_url} 
                      alt={name}
                      className="w-64 h-64 md:w-80 md:h-80 object-cover"
                    />
                  ) : (
                    <div 
                      className="w-64 h-64 md:w-80 md:h-80 flex items-center justify-center bg-white/10 backdrop-blur-sm"
                    >
                      {group.type === "whatsapp" ? (
                        <WhatsAppIcon className="w-32 h-32 text-white" />
                      ) : (
                        <TelegramIcon className="w-32 h-32 text-white" />
                      )}
                    </div>
                  )}
                  
                  {/* Platform Badge */}
                  <div 
                    className="absolute top-4 right-4 px-4 py-2 rounded-full text-white font-bold flex items-center gap-2"
                    style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)' }}
                  >
                    {group.type === "whatsapp" ? (
                      <>
                        <MessageSquare className="w-5 h-5" />
                        WhatsApp
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Telegram
                      </>
                    )}
                  </div>
                  
                  {/* Members Badge */}
                  <div 
                    className="absolute bottom-4 left-4 px-4 py-2 rounded-full text-white font-medium flex items-center gap-2"
                    style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)' }}
                  >
                    <Users className="w-5 h-5" />
                    {group.members.toLocaleString()} {isRTL ? "عضو" : "members"}
                  </div>
                </div>
              </motion.div>

              {/* Content */}
              <motion.div 
                className="text-white"
                initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h1 className="text-3xl md:text-4xl font-bold mb-4">{name}</h1>
                
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="px-4 py-2 rounded-full flex items-center gap-2"
                    style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)' }}
                  >
                    <Users className="w-5 h-5" />
                    <span className="font-semibold">
                      {group.members.toLocaleString()} {isRTL ? "عضو" : "members"}
                    </span>
                  </div>
                </div>

                {description && (
                  <p className="text-white/90 mb-6 leading-relaxed text-lg">{description}</p>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4">
                  {user ? (
                    <Button
                      onClick={handleJoinGroup}
                      size="lg"
                      className="text-white font-bold px-8 border-0"
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <ExternalLink className="w-5 h-5" />
                      {isRTL 
                        ? `انضم عبر ${platformName}` 
                        : `Join on ${platformName}`}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setIsLoginPromptOpen(true)}
                      size="lg"
                      className="text-white font-bold px-8 border-0"
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <Lock className="w-5 h-5" />
                      {isRTL ? "سجل للانضمام" : "Register to Join"}
                    </Button>
                  )}

                  {/* Share Button */}
                  <div className="relative">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => {
                        setShowShareMenu(!showShareMenu);
                        setCopied(false);
                      }}
                      className="border-white/30 text-white hover:bg-white/10"
                    >
                      <Share2 className="w-5 h-5" />
                      {isRTL ? "مشاركة" : "Share"}
                    </Button>

                    <AnimatePresence>
                      {showShareMenu && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9, y: 10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.9, y: 10 }}
                          className="absolute top-full mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-3 flex gap-3 z-30"
                        >
                          <a
                            href={`https://twitter.com/intent/tweet?text=${encodedShareMessage}&url=${encodedShareUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-3 rounded-full bg-black text-white hover:bg-gray-800 transition-colors"
                            title="Share on X"
                          >
                            <XIcon />
                          </a>
                          <a
                            href={`https://t.me/share/url?url=${encodedShareUrl}&text=${encodedShareMessage}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-3 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                            title="Share on Telegram"
                          >
                            <TelegramIcon className="w-5 h-5" />
                          </a>
                          <a
                            href={`https://wa.me/?text=${encodedShareMessage}%20${encodedShareUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-3 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors"
                            title="Share on WhatsApp"
                          >
                            <WhatsAppIcon className="w-5 h-5" />
                          </a>
                          <a
                            href={`mailto:?subject=${encodeURIComponent(name)}&body=${encodedShareMessage}%20${encodedShareUrl}`}
                            className="p-3 rounded-full bg-gray-500 text-white hover:bg-gray-600 transition-colors"
                            title="Share via Email"
                          >
                            <Mail className="w-5 h-5" />
                          </a>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(shareUrl);
                              setCopied(true);
                              toast.success(isRTL ? "تم نسخ الرابط" : "Link copied!");
                              setTimeout(() => setCopied(false), 2000);
                            }}
                            className={`p-3 rounded-full transition-colors ${copied ? 'bg-green-500 text-white' : 'bg-purple-500 text-white hover:bg-purple-600'}`}
                            title={isRTL ? "نسخ الرابط" : "Copy Link"}
                          >
                            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="container mx-auto px-4 py-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/social-groups")}
            className="gap-2"
          >
            {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
            {isRTL ? "العودة للمجموعات" : "Back to Groups"}
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SocialGroupDetails;
